import { FocusTrap, inertHelper, isMobile, isServerRendering, ScrollLock, Swipeable, SwipeDirection } from '@farmasiet/frontendcore-reactcomponents';
import {
    CloseModalMessage,
    DynamicHeightInModalMessage,
    GetEcomDataResponseMessage,
    IFrameMessageCallbackType,
    IFrameMessageSender,
    InitialModalSize,
    MessageType,
    MoveFocusIntoIframeMessage,
    PhilModalTheme,
    PhilModalVariant,
    RequestForFocusOutsideIframeMessage,
    ShowModalMessage,
    useMessageReceivers,
    ViewChangedMessage
} from '@farmasiet/iframe-messages';
import classNames from 'classnames';
import React, { MouseEvent, useEffect, useRef, useState } from 'react';

import { CustomerSupportContactInfo } from '../CustomerSupportContactInfo/CustomerSupportContactInfo';
import { ErrorView } from '../ErrorView/ErrorView';
import { PhilModalPlaceholder } from './PhilModalPlaceholder/PhilModalPlaceholder';

interface IPhilModal {
    isVisible: boolean;
    hideModal: () => void;
    customerServiceLabel: string;
    customerServicePhoneNumber: string;
    customerServiceUrl: string;
    showModalProps: ShowModalMessage;
    philUrl: string;
    version: string;
    rxData: Record<string, unknown>;
    callback: (value: IFrameMessageCallbackType) => void;
}

export const PhilModal = ({
    isVisible,
    hideModal,
    customerServiceLabel,
    customerServicePhoneNumber,
    customerServiceUrl,
    showModalProps,
    philUrl,
    version,
    rxData,
    callback
}: IPhilModal) => {
    const [scrollLockActive, setScrollLockActive] = useState(false);
    const [showLoadingSkeleton, setShowLoadingSkeleton] = useState(false);
    const [height, setHeight] = useState<number | null>(null);
    const [showIframe, setShowIframe] = useState(false);
    const isMobileRef = useRef(isMobile());
    const modalRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const messageSenderRef = useRef<IFrameMessageSender>(IFrameMessageSender.create(showModalProps.url, iframeRef));
    const wasShownRef = useRef<boolean>(false);
    const modalVariantRef = useRef<PhilModalVariant>(showModalProps.variant);

    useEffect(() => {
        setShowLoadingSkeleton(isVisible);
        handleScrollLock(isVisible);

        if (isVisible) {
            isMobileRef.current = isMobile();
            wasShownRef.current = true;
            setShowIframe(true);
        } else {
            // reset height after closing modal
            setHeight(null);
        }

        if (!isServerRendering()) {
            setOverscollBehaviour(document, isVisible ? 'none' : '');

            return () => {
                setOverscollBehaviour(document, '');
            };
        }
    }, [isVisible]);

    useEffect(() => {
        messageSenderRef.current.sendMessage(new ViewChangedMessage(isMobile()));
    }, [isMobile()]);

    useEffect(() => {
        messageSenderRef.current = IFrameMessageSender.create(showModalProps.url, iframeRef);
    }, [showModalProps.url]);

    useEffect(() => {
        modalVariantRef.current = showModalProps.variant;
    }, [showModalProps.variant]);

    useMessageReceivers(philUrl, {
        [MessageType.SET_HEIGHT_IN_MODAL]: (message: DynamicHeightInModalMessage) => setHeight(message.size),
        [MessageType.CLOSE_MODAL]: (message: CloseModalMessage) => {
            hideModal();
            if (message.value) {
                callback(message.value);
            }
        },
        [MessageType.REQUEST_FOR_FOCUS_OUTSIDE_IFRAME]: (message: RequestForFocusOutsideIframeMessage) =>
            handleRequestForFocusOutsideIframe(message.focusAfter),
        [MessageType.GET_ECOM_DATA_REQUEST]: () => messageSenderRef.current.sendMessage(new GetEcomDataResponseMessage(rxData))
    });

    const handleRequestForFocusOutsideIframe = (focusAfter: boolean) => {
        if (modalVariantRef.current !== PhilModalVariant.Center) {
            const customerSupportLink = modalRef.current?.querySelector('#CustomerSupportContactInfo_Link');
            if (customerSupportLink) {
                (customerSupportLink as HTMLAnchorElement).focus();
                return;
            }
        }
        moveFocusIntoIframe(focusAfter);
    };

    const moveFocusIntoIframe = (atBeginning: boolean) => {
        messageSenderRef.current.sendMessage(new MoveFocusIntoIframeMessage(atBeginning));
    };

    const moveFocusIntoCustomerSupport = () => {
        const customerSupportLink = modalRef.current?.querySelector('#CustomerSupportContactInfo_Link');
        if (customerSupportLink) {
            (customerSupportLink as HTMLAnchorElement).focus();
        }
    };

    const setOverscollBehaviour = (document: Document, value: string) => {
        document.documentElement.style.overscrollBehavior = value;
        document.body.style.overscrollBehavior = value;
    };

    const handleScrollLock = (isModalVisible: boolean) => {
        isModalVisible ? setScrollLockActive(true) : setTimeout(() => setScrollLockActive(false), 300);
    };

    const handleSwipe = (swipeDirection: SwipeDirection) => {
        if (isMobile()) {
            if (swipeDirection === SwipeDirection.Bottom) {
                hideModal();
            }
        } else if (
            (showModalProps.variant === PhilModalVariant.Left && swipeDirection === SwipeDirection.Left) ||
            (showModalProps.variant === PhilModalVariant.Right && swipeDirection === SwipeDirection.Right)
        ) {
            hideModal();
        }
    };

    const modalVariantCssClass = (): string => {
        switch (showModalProps.variant) {
            case PhilModalVariant.Left:
                return 'PhilModal__Inner--Left';
            case PhilModalVariant.Right:
                return 'PhilModal__Inner--Right';
            case PhilModalVariant.Center:
                return 'PhilModal__Inner--Center';
            default:
                return 'PhilModal__Inner--NoVariant';
        }
    };

    const modalThemeCssClass = (): string => {
        switch (showModalProps.theme) {
            case PhilModalTheme.LightBlue:
                return 'PhilModal__Inner--LightBlue';
            default:
                return 'PhilModal__Inner--Default';
        }
    };

    const closeModal = () => {
        hideModal();
    };

    const handleClickOutside = (event: MouseEvent<HTMLElement>) => {
        const modal = modalRef.current;

        if (modal && !modal.contains(event.target as Node)) {
            closeModal();
        }
    };

    const buildUrlWithParams = (url: string): URL => {
        const iframeUrl = new URL(url);
        iframeUrl.searchParams.set('isMobile', isMobileRef.current.toString());
        iframeUrl.searchParams.set('v', version);
        return iframeUrl;
    };

    const getStyles = () => {
        if (height) {
            return { height: height, maxHeight: 'initial' };
        }
    };

    const onAnimationEnd = () => {
        if (!isVisible) {
            setShowIframe(false);
        }
    };

    const hideLoadingSkeleton = () => {
        setShowLoadingSkeleton(false);
    };

    return (
        <Swipeable onSwipe={handleSwipe}>
            <FocusTrap enabled={isVisible}>
                <aside
                    className={classNames('PhilModal', !isVisible && 'PhilModal--Hidden', wasShownRef.current && 'PhilModal--WasShown')}
                    onClick={handleClickOutside}
                    onAnimationEnd={onAnimationEnd}
                    {...inertHelper(isVisible)}
                >
                    <ScrollLock isLocked={isVisible || scrollLockActive} />

                    <div
                        ref={modalRef}
                        className={classNames(
                            'PhilModal__Inner',
                            modalVariantCssClass(),
                            modalThemeCssClass(),
                            showModalProps.prescriptionHistoryStyle && 'PhilModal__Inner--PrescriptionHistoryFilters',
                            showModalProps.initialModalSize === InitialModalSize.DatePicker && 'PhilModal__DatePicker',
                            showModalProps.initialModalSize === InitialModalSize.Small && 'PhilModal__Inner--ShorterHeight'
                        )}
                        role="dialog"
                        aria-modal
                        aria-hidden={!isVisible}
                    >
                        <div className="PhilModal__DragHandle" />
                        <div
                            className={classNames(
                                'PhilModal__SwipeableOverlay',
                                showModalProps.shorterSwipeableOverlay && 'PhilModal__ShorterSwipeableOverlay'
                            )}
                        />
                        {showModalProps.url ? (
                            <>
                                <div
                                    className={classNames(
                                        'PhilModal__IFrame',
                                        showModalProps.initialModalSize === InitialModalSize.Small && 'PhilModal__IFrame--ShorterInitialHeight',
                                        showModalProps.initialModalSize === InitialModalSize.DatePicker && 'PhilModal__DatePicker',
                                        showLoadingSkeleton && 'PhilModal__IFrame--IFrameHidden'
                                    )}
                                    style={getStyles()}
                                    key={showModalProps.key}
                                >
                                    {showLoadingSkeleton && <PhilModalPlaceholder variant={showModalProps.variant} isMobile={isMobileRef.current} />}
                                    {showIframe && (
                                        <iframe
                                            src={buildUrlWithParams(showModalProps.url).href}
                                            sandbox="allow-scripts allow-same-origin allow-popups allow-downloads"
                                            ref={iframeRef}
                                            onLoad={hideLoadingSkeleton}
                                        />
                                    )}
                                </div>
                                <CustomerSupportContactInfo
                                    customerServiceLabel={customerServiceLabel}
                                    customerServicePhoneNumber={customerServicePhoneNumber}
                                    customerServiceUrl={customerServiceUrl}
                                    linkKeyDownHandle={moveFocusIntoIframe}
                                    hideModal={hideModal}
                                />
                            </>
                        ) : (
                            <ErrorView
                                hideModal={hideModal}
                                variant={showModalProps.variant}
                                customerServiceLabel={customerServiceLabel}
                                customerServicePhoneNumber={customerServicePhoneNumber}
                                customerServiceUrl={customerServiceUrl}
                                buttonKeyDownHandle={moveFocusIntoCustomerSupport}
                            />
                        )}
                    </div>
                </aside>
            </FocusTrap>
        </Swipeable>
    );
};
